import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from 'sonner';

import { IconContext } from './lib/icon-context';
import { App } from './App';
import { ApiRequestError } from './lib/api/client';
import { AuthProvider } from './lib/auth';
import { I18nProvider } from './lib/i18n';
import { PreferencesProvider } from './lib/preferences';
import { OfflineProvider } from './lib/offline/context';
import {
  CACHE_BUSTER,
  createIdbPersister,
  dehydrateOptions,
  MAX_CACHE_AGE_MS,
} from './lib/offline/persister';
import { TooltipProvider } from './components/ui/misc';
import './styles/globals.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Persisted entries must outlive the tab, otherwise there is nothing to restore
      // from after a reload while offline.
      gcTime: MAX_CACHE_AGE_MS,
      refetchOnWindowFocus: false,
      // Refetching on reconnect is how the cached book catches up after an outage.
      refetchOnReconnect: true,
      retry: (failureCount, error) => {
        // Retrying a 4xx just repeats a rejected request; only transient faults
        // (network, 5xx) are worth a second attempt.
        if (error instanceof ApiRequestError && error.status >= 400 && error.status < 500) return false;
        // Offline, a retry cannot succeed — fall through to the cache instead of
        // burning attempts against a dead connection.
        if (!navigator.onLine) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

const persister = createIdbPersister();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          maxAge: MAX_CACHE_AGE_MS,
          // Any change to this string discards every persisted cache — the escape hatch
          // if a DTO shape changes and old cached rows would no longer render.
          buster: CACHE_BUSTER,
          dehydrateOptions,
        }}
      >
        <PreferencesProvider>
          <I18nProvider>
            <AuthProvider>
              <OfflineProvider>
                <TooltipProvider delayDuration={300}>
                  <IconContext>
                    <App />
                  </IconContext>
                  <Toaster
                    position="bottom-right"
                    closeButton
                    richColors
                    toastOptions={{ className: 'font-sans' }}
                  />
                </TooltipProvider>
              </OfflineProvider>
            </AuthProvider>
          </I18nProvider>
        </PreferencesProvider>
      </PersistQueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
